import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subject, catchError, map, of, switchMap, tap } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import { HourglassSpinnerComponent } from '@shared/hourglass-spinner/hourglass-spinner.component';
import { SuggestionChipComponent } from '@shared/suggestion-chip/suggestion-chip.component';
import {
  DOCUMENT_UPLOAD,
  DOCUMENT_UPLOAD_STATES,
  DocumentUploadResponse,
  DocumentUploadStatus,
  IDLE_DOCUMENT_STATUS,
  getDocumentErrorKey,
  getDocumentRejectionKey,
  validateDocumentFile,
} from '@core/constants/guided-course-creation.constants';

interface UploadRequest {
  file: File;
  sessionId: string;
}

interface TopicChip {
  topic: string;
  used: boolean;
}

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    TranslateModule,
    TooltipButtonComponent,
    HourglassSpinnerComponent,
    SuggestionChipComponent,
  ],
})
export class DocumentUploadComponent {
  readonly acceptedFileTypes = DOCUMENT_UPLOAD.ACCEPTED_FILE_TYPES;
  readonly messageParams = { size: DOCUMENT_UPLOAD.MAX_SIZE_LABEL };

  readonly sessionId = input.required<string | null>();
  readonly disabled = input(false);
  readonly currentTopic = input('');

  readonly statusChange = output<DocumentUploadStatus>();
  readonly topicSelected = output<string>();

  readonly selectedFile = signal<File | null>(null);
  readonly isUploading = signal(false);
  readonly pageCount = signal<number | null>(null);
  readonly suggestedTopics = signal<readonly string[]>([]);
  // Kept for the delete endpoint the backend has not shipped yet.
  readonly documentId = signal<string | null>(null);
  private readonly errorKey = signal<string | null>(null);

  readonly isUploadDisabled = computed(
    () => this.disabled() || !!this.selectedFile(),
  );

  readonly messageKey = computed(() => this.errorKey() ?? '');

  readonly hasError = computed(() => !!this.messageKey());

  readonly topicChips = computed<readonly TopicChip[]>(() =>
    this.suggestedTopics().map(topic => ({
      topic,
      used: this.currentTopic().includes(topic),
    })),
  );

  private readonly gccService = inject(GuidedCourseCreationService);

  private readonly fileRequest = new Subject<UploadRequest | null>();

  constructor() {
    this.fileRequest
      .pipe(
        tap(request => (request ? this.beginUpload() : this.resetState())),
        switchMap(request =>
          request
            ? this.gccService
                .uploadDocument(request.sessionId, request.file)
                .pipe(
                  map(response => ({ ok: true as const, response })),
                  // Inside switchMap: on the outer pipe a failure would end the subject.
                  catchError((error: HttpErrorResponse) =>
                    of({ ok: false as const, error }),
                  ),
                )
            : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(result =>
        result.ok
          ? this.handleAnalysis(result.response)
          : this.handleHttpError(result.error),
      );
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Without this, re-picking the same file never fires `change` again.
    input.value = '';

    const sessionId = this.sessionId();
    if (!file || this.selectedFile() || !sessionId) return;

    const rejectionReason = validateDocumentFile(file);
    if (rejectionReason) {
      this.resetState();
      this.selectedFile.set(file);
      this.errorKey.set(getDocumentRejectionKey(rejectionReason));
      this.statusChange.emit({
        state: DOCUMENT_UPLOAD_STATES.ERROR,
        suggestedTopics: [],
      });
      return;
    }

    this.selectedFile.set(file);
    this.fileRequest.next({ file, sessionId });
  }

  onSelectTopic(topic: string): void {
    this.topicSelected.emit(topic);
  }

  onRemove(): void {
    this.fileRequest.next(null);
    this.selectedFile.set(null);
    this.statusChange.emit(IDLE_DOCUMENT_STATUS);
  }

  private beginUpload(): void {
    this.resetState();
    this.isUploading.set(true);
    this.statusChange.emit({
      state: DOCUMENT_UPLOAD_STATES.UPLOADING,
      suggestedTopics: [],
    });
  }

  private resetState(): void {
    this.documentId.set(null);
    this.errorKey.set(null);
    this.pageCount.set(null);
    this.suggestedTopics.set([]);
    this.isUploading.set(false);
  }

  private handleAnalysis(response: DocumentUploadResponse): void {
    this.isUploading.set(false);
    this.documentId.set(response.id);
    this.pageCount.set(response.page_count ?? null);

    const suggestedTopics = response.analysis?.suggested_topics ?? [];
    this.suggestedTopics.set(suggestedTopics);
    this.statusChange.emit({
      state: DOCUMENT_UPLOAD_STATES.DONE,
      suggestedTopics,
    });
  }

  private handleHttpError(error: HttpErrorResponse): void {
    this.isUploading.set(false);
    this.errorKey.set(getDocumentErrorKey(error));
    this.statusChange.emit({
      state: DOCUMENT_UPLOAD_STATES.ERROR,
      suggestedTopics: [],
    });
  }
}
